"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SpecializedStemCurriculum() {
  const modules = [{
    number: "01.",
    name: "CALCULUS MASTERY",
    description: "We demystify the language of change. From limits to differential equations, our approach builds a rigorous foundation that transforms complex theory into intuitive problem-solving.",
    deliverables: ["Conceptual limit foundations", "Derivative application strategies", "Integral calculus visualization", "Advanced problem-set deconstruction"],
    cta: "EXPLORE CALCULUS TRACKS",
    stat: "95% OF STUDENTS IMPROVE BY 2+ LETTER GRADES",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/calculus_whiteboard_equations.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/calculus_whiteboard_equations.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/calculus_whiteboard_equations.png-wvc-sizes",
      alt: "Complex calculus equations written on a digital whiteboard"
    }
  }, {
    number: "02.",
    name: "AP PHYSICS",
    description: "Physics is the study of reality. We bridge the gap between abstract formulas and physical phenomena, ensuring you understand the 'why' behind every mechanical interaction.",
    deliverables: ["Kinematics and dynamics mastery", "Electromagnetism core principles", "Lab report analytical precision", "AP exam simulation and pacing"],
    cta: "EXPLORE PHYSICS TRACKS",
    stat: "AVERAGE AP SCORE INCREASE OF 1.4 POINTS",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/physics_lab_diagrams.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/physics_lab_diagrams.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/physics_lab_diagrams.webp-wvc-sizes",
      alt: "Physics force diagrams and motion equations"
    }
  }, {
    number: "03.",
    name: "SAT MATH STRATEGY",
    description: "Standardized testing is a game of pattern recognition. We teach you to identify the underlying logic of every question type, maximizing your efficiency and score potential.",
    deliverables: ["Question pattern recognition", "Time-management optimization", "Calculator-free speed drills", "Diagnostic-driven curriculum"],
    cta: "EXPLORE SAT STRATEGIES",
    stat: "200+ POINT AVERAGE SCORE IMPROVEMENT",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/sat_math_prep.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/sat_math_prep.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/sat_math_prep.webp-wvc-sizes",
      alt: "Student working on SAT math practice problems"
    }
  }];
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3508" id="specialized-stem-curriculum" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        {}
        <div ref={header.ref} className={`transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-[12px] uppercase tracking-[0.2em] mb-10 md:mb-14" style={{
          color: "var(--primary)"
        }}>
            OUR EXPERTISE — 03 DISCIPLINES
          </p>
          <h2 className="font-default font-extrabold leading-[0.95] tracking-tight text-foreground text-[44px] sm:text-[64px] md:text-[84px] lg:text-[96px]">
            <span className="block">WHAT WE</span>
            <span className="block">DO WITH</span>
            <span className="block" style={{
            WebkitTextStroke: "1.5px var(--primary)",
            color: "transparent"
          }}>
              ACADEMIC
            </span>
            <span className="block">ASCENT.</span>
          </h2>
        </div>

        {}
        <div className="mt-24 md:mt-32 lg:mt-40 flex flex-col gap-24 md:gap-32 lg:gap-36">
          {modules.map((m, i) => {
          const reveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          const isReversed = i % 2 === 1;
          return <div key={i} data-index={i} ref={reveal.ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-700 ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
                {}
                <div className={`order-2 ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
                  <div className={`font-default font-extrabold leading-none tracking-tight text-[64px] md:text-[88px] lg:text-[120px] transition-transform duration-1000 ease-out ${reveal.isVisible ? "-translate-y-2" : "translate-y-4"}`} style={{
                WebkitTextStroke: "1.5px var(--primary)",
                color: "transparent"
              }}>
                    {m.number}
                  </div>

                  <h3 className="font-default font-extrabold leading-[0.95] tracking-tight text-foreground text-[36px] md:text-[48px] lg:text-[56px] mt-4 md:mt-6">
                    {m.name}
                  </h3>

                  <p className="font-default font-light text-foreground/90 text-lg md:text-xl leading-relaxed mt-6 max-w-[480px]">
                    {m.description}
                  </p>

                  <ul className="mt-8 flex flex-col gap-3 max-w-[480px]">
                    {m.deliverables.map((d, di) => <li key={di} data-index={di} className="flex items-start gap-3 font-default text-base text-muted-foreground">
                        <span className="inline-block mt-[10px] shrink-0" style={{
                    width: "5px",
                    height: "5px",
                    backgroundColor: "var(--primary)"
                  }} aria-hidden="true" />
                        <span>{d}</span>
                      </li>)}
                  </ul>

                  <div className="mt-10">
                    <Link to="/MasteryTracksPage">
                      <Button variant="ghost" className="px-0 h-auto py-2 font-default font-semibold uppercase tracking-[0.15em] text-sm hover:bg-transparent group" style={{
                    color: "var(--primary)"
                  }}>
                        {m.cta}
                        <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </Button>
                    </Link>
                  </div>
                </div>

                {}
                <div className={`order-1 relative ${isReversed ? "lg:order-1" : "lg:order-2"}`}>
                  <div className="relative aspect-[3/4] w-full overflow-hidden" style={{
                boxShadow: "var(--shadow-4xl)"
              }}>
                    <img src={m.image.src} data-wvc-srcset={m.image.srcset} data-wvc-sizes={m.image.sizes} alt={m.image.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />

                    {}
                    <div className="absolute left-6 right-6 bottom-6 md:left-8 md:right-auto md:bottom-8 md:max-w-[320px] backdrop-blur-md px-5 py-4 border" style={{
                  backgroundColor: "hsla(225, 73%, 20%, 0.85)",
                  borderColor: "hsla(225, 15%, 94%, 0.18)"
                }}>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-1" style={{
                    color: "var(--secondary)"
                  }}>
                        BY THE NUMBERS
                      </p>
                      <p className="font-default font-bold text-white text-sm md:text-base leading-snug">
                        {m.stat}
                      </p>
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}